import { useEffect, useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, AlertTriangle, CheckCircle, Info, Layers, Loader2, LogIn, PenLine, RotateCcw, Undo2 } from 'lucide-react';
import Button from '../../ui/Button';
import { microInteractions, springs } from '../../../utils/animations';
import { TONE_CLASSES } from '../../../utils/tones';
import { fileBaseName, retryModeFor, tileStatusFor } from '../../../utils/playlistDelivery';
import {
  COPY,
  DELIVERY_COPY,
  backOnline,
  buildingDetail,
  buildingLine,
  removedUndo,
  retryGone,
  retryOffline,
  sendingLine,
  successDetail,
  successLine,
} from './playlistCopy';

export const UNDO_WINDOW_MS = 6000;

// Toast.jsx `bgClasses`, plus a neutral surface for the in-progress states.
const SURFACES = {
  progress: 'bg-white/80 dark:bg-elevated-dark-2 border-gray-200 dark:border-white/10',
  success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
  error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  muted: 'bg-gray-50 dark:bg-elevated-dark-2 border-gray-200 dark:border-white/10',
};

const FAILURE_TONE = { timeout: 'warning', not_available: 'muted' };
const SEND_FAILURES = ['item', 'gone', 'offline'];
const BUILD_FAILED_FILL = { warning: 'bg-amber-500', muted: 'bg-gray-400 dark:bg-gray-500' };

const SEGMENT_FILL = {
  sent: 'bg-primary-500',
  sending: 'bg-primary-500',
  queued: 'bg-gray-200 dark:bg-white/10',
  failed: 'bg-red-500',
  gone: 'bg-red-500',
  missing: 'bg-red-500',
  idle: 'bg-gray-200 dark:bg-white/10',
};

function UndoCountdown() {
  const [seconds, setSeconds] = useState(UNDO_WINDOW_MS / 1000);
  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(id);
  }, []);
  return <> ({seconds}s)</>;
}

/**
 * The carousel's inline status line: the Undo bar after a remove (Lane 05)
 * and, once a delivered playlist is edited, R-2. The delivery itself shows in
 * `DeliveryPanel`; while it does, this line keeps only Undo.
 */
export default function DeliveryStatusStrip({ undo, onUndo, edited = false, panelShown = false }) {
  const reduceMotion = useReducedMotion();
  const testId = panelShown ? 'playlist-undo-bar' : 'playlist-delivery-status';

  return (
    <AnimatePresence mode="popLayout">
      {undo ? (
        <motion.div
          key={undo.item.itemKey}
          {...(reduceMotion ? microInteractions.fadeIn : microInteractions.slideUp)}
          role="status"
          data-testid={testId}
          className="relative overflow-hidden flex items-center gap-2 squircle-sm px-3 py-1.5 border bg-gray-50 dark:bg-elevated-dark-2 border-gray-200 dark:border-white/10 min-w-0"
        >
          <span className="text-body-sm text-gray-700 dark:text-gray-200 truncate min-w-0">
            {removedUndo(undo.item.title)} ·
          </span>
          <button
            type="button"
            onClick={onUndo}
            data-testid="playlist-undo-button"
            className="inline-flex items-center gap-1 flex-shrink-0 px-2 py-0.5 rounded-md text-body-sm font-semibold text-primary-700 dark:text-primary-300 hover:bg-primary-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Undo2 className="w-3.5 h-3.5" strokeWidth={2.5} />
            {COPY.undo}
            {reduceMotion && <UndoCountdown />}
          </button>
          {!reduceMotion && (
            <motion.span
              aria-hidden="true"
              className="absolute left-0 bottom-0 h-0.5 w-full origin-left bg-primary-500"
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: UNDO_WINDOW_MS / 1000, ease: 'linear' }}
            />
          )}
        </motion.div>
      ) : edited && !panelShown ? (
        <motion.p
          key="edited"
          {...microInteractions.fadeIn}
          role="status"
          data-testid="playlist-delivery-status"
          data-status="idle"
          className={`flex items-center gap-1.5 text-body-sm min-w-0 ${TONE_CLASSES.muted}`}
        >
          <PenLine className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span className="truncate">{DELIVERY_COPY.edited}</span>
        </motion.p>
      ) : null}
    </AnimatePresence>
  );
}

function ProgressSegments({ delivery, items, tone, reduceMotion }) {
  const { status, progressStage } = delivery;
  const done = status === 'done';
  const buildFailed = status === 'failed' && !SEND_FAILURES.includes(delivery.failedKind);
  const building = status === 'building';
  const buildReached = building || done || buildFailed;

  return (
    <div className="mt-3 flex items-center gap-1" aria-hidden="true" data-testid="playlist-delivery-segments">
      {items.map((item) => {
        const tile = tileStatusFor(delivery, item.itemKey);
        const active = tile === 'sending';
        return (
          <span
            key={item.itemKey}
            data-segment={tile}
            className={`h-1.5 flex-1 min-w-[3px] rounded-full transition-colors duration-300 ease-out ${
              done ? 'bg-emerald-500' : SEGMENT_FILL[tile]
            } ${active && !reduceMotion ? 'animate-pulse-slow' : ''}`}
          />
        );
      })}
      <span
        data-segment="build"
        className={`relative overflow-hidden h-1.5 flex-[1.5] min-w-[6px] rounded-full transition-colors duration-300 ease-out ${
          done
            ? 'bg-emerald-500'
            : buildFailed
              ? BUILD_FAILED_FILL[tone] ?? 'bg-red-500'
              : building
                ? (progressStage === 'done' ? 'bg-primary-500' : 'bg-primary-300 dark:bg-primary-700')
                : 'bg-gray-200 dark:bg-white/10'
        } ${building && progressStage !== 'done' && !reduceMotion ? 'shimmer' : ''}`}
        data-reached={buildReached}
      />
    </div>
  );
}

/**
 * S5–S7: the delivery ceremony's status block at the top of the dock —
 * progress while sending and building, the success moment, and every
 * failure with the action that follows from it.
 */
export function DeliveryPanel({
  delivery,
  items,
  stationName,
  stationOnline,
  libraryText,
  compact = false,
  onRetry,
  onChooseStation,
  onDeliverAgain,
  onNew,
  onSignIn,
}) {
  const reduceMotion = useReducedMotion();
  const reasonId = useId();
  const { status, failedKind } = delivery;
  const n = items.length;

  let tone = 'progress';
  let Icon = Loader2;
  let primary = null;
  let detail = null;

  if (status === 'sending') {
    const index = Math.max(items.findIndex((item) => item.itemKey === delivery.currentKey), 0);
    primary = sendingLine(index + 1, n, items[index]?.title ?? '');
  } else if (status === 'building') {
    Icon = Layers;
    primary = buildingLine(stationName);
    detail = delivery.progressLabel || buildingDetail(n);
  } else if (status === 'done') {
    tone = 'success';
    Icon = CheckCircle;
    primary = successLine(delivery.groupCount ?? n, stationName);
    detail = successDetail(libraryText, fileBaseName(delivery.proFilePath));
  } else if (status === 'failed') {
    tone = FAILURE_TONE[failedKind] ?? 'error';
    Icon = tone === 'warning' ? AlertTriangle : tone === 'muted' ? Info : AlertCircle;
    const recovered = failedKind === 'offline' && stationOnline;
    primary = recovered ? backOnline(stationName) : delivery.errorMessage;
    if (failedKind === 'timeout') detail = DELIVERY_COPY.timeoutDetail;
    else if (failedKind === 'not_available') detail = DELIVERY_COPY.notAvailableDetail;
    else if (failedKind === 'missing') {
      detail = items.filter((item) => delivery.missingItemKeys.includes(item.itemKey)).map((item) => item.title).join(', ');
    } else if (failedKind !== 'gone') detail = delivery.errorDetail;
  }

  const goneItem = failedKind === 'gone' && items.find((item) => item.itemKey === delivery.failedKey);
  const retryBlocked = goneItem
    ? retryGone(goneItem.title)
    : failedKind === 'offline' && !stationOnline
      ? retryOffline(stationName)
      : null;
  const mode = retryModeFor(failedKind);
  const showRetry = status === 'failed' && mode !== 'none' && failedKind !== 'not_available';
  const showChoose = status === 'failed' && !['not_available', 'auth', 'invalid'].includes(failedKind);
  const iconTone = { progress: 'text-primary-600 dark:text-primary-400', success: 'text-emerald-600 dark:text-emerald-400', error: 'text-red-600 dark:text-red-400', warning: 'text-amber-600 dark:text-amber-400', muted: 'text-gray-500 dark:text-gray-400' }[tone];
  const spin = Icon === Loader2 && !reduceMotion;

  const actions = (
    <>
      {showRetry && (
        <Button
          size="sm"
          variant="primary"
          onClick={onRetry}
          disabled={Boolean(retryBlocked)}
          aria-describedby={retryBlocked ? reasonId : undefined}
          data-testid="playlist-retry-button"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" aria-hidden="true" />
          {DELIVERY_COPY.retry}
        </Button>
      )}
      {failedKind === 'not_available' && status === 'failed' && (
        <Button size="sm" variant="ghost" onClick={onRetry} data-testid="playlist-try-later-button">
          {DELIVERY_COPY.tryLater}
        </Button>
      )}
      {failedKind === 'auth' && status === 'failed' && (
        <Button size="sm" variant="primary" onClick={onSignIn} data-testid="playlist-sign-in-button">
          <LogIn className="w-4 h-4 mr-1.5" aria-hidden="true" />
          {DELIVERY_COPY.signIn}
        </Button>
      )}
      {showChoose && (
        <Button size="sm" variant="ghost" onClick={onChooseStation} data-testid="playlist-choose-station-button">
          {DELIVERY_COPY.chooseAnother}
        </Button>
      )}
      {status === 'done' && (
        <>
          <Button size="sm" variant="secondary" onClick={onDeliverAgain} data-testid="playlist-deliver-again-button">
            {DELIVERY_COPY.deliverAgain}
          </Button>
          <Button size="sm" variant="ghost" onClick={onNew} data-testid="playlist-new-button">
            {DELIVERY_COPY.startNew}
          </Button>
        </>
      )}
    </>
  );
  const hasActions = status === 'done' || status === 'failed';

  return (
    <div
      role="status"
      tabIndex={-1}
      data-testid="playlist-delivery-status"
      data-delivery-strip
      data-status={status}
      data-kind={failedKind ?? undefined}
      data-tone={tone}
      data-progress-stage={delivery.progressStage ?? undefined}
      className={`relative squircle-sm border px-4 py-3 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${SURFACES[tone]}`}
    >
      <div
        className={`flex gap-3 ${compact ? 'flex-col' : 'items-start'}`}
        data-testid={status === 'done' ? 'playlist-success' : undefined}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={`${status}-${tone}`}
              initial={reduceMotion ? false : { scale: status === 'done' ? 0 : 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={reduceMotion ? undefined : { opacity: 0 }}
              transition={status === 'done' ? { type: 'spring', stiffness: 200, damping: 10 } : { duration: 0.15 }}
              className={`mt-0.5 flex-shrink-0 ${iconTone}`}
              aria-hidden="true"
            >
              <Icon className={`w-5 h-5 ${spin ? 'animate-spin' : ''}`} />
            </motion.span>
          </AnimatePresence>
          <div className="min-w-0 flex-1">
            <AnimatePresence mode="wait" initial={false}>
              <motion.p
                key={primary}
                initial={reduceMotion ? false : { y: 6, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={reduceMotion ? undefined : { y: -6, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className={`text-body-sm font-semibold ${status === 'sending' ? 'truncate' : 'break-words'} ${
                  tone === 'progress' ? 'text-gray-900 dark:text-gray-100' : TONE_CLASSES[tone]
                }`}
                data-testid="playlist-delivery-primary"
              >
                {primary}
              </motion.p>
            </AnimatePresence>
            {detail && (
              <p className="mt-0.5 text-caption text-gray-600 dark:text-gray-300 break-words" data-testid="playlist-delivery-detail">
                {detail}
              </p>
            )}
            {retryBlocked && (
              <p id={reasonId} className={`mt-0.5 text-caption ${TONE_CLASSES.muted}`}>{retryBlocked}</p>
            )}
          </div>
        </div>
        {hasActions && (
          <motion.div
            className={`flex flex-wrap items-center gap-2 flex-shrink-0 ${compact ? 'pl-8' : ''}`}
            {...(status === 'done' && !reduceMotion ? { initial: { y: 20, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: springs.gentle } : {})}
          >
            {actions}
          </motion.div>
        )}
      </div>
      <ProgressSegments delivery={delivery} items={items} tone={tone} reduceMotion={reduceMotion} />
    </div>
  );
}
